import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useMultiTenant } from '@/hooks/useMultiTenant';
import { Card } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Package, Search } from 'lucide-react';
import { format } from 'date-fns';

interface RentalItem {
  id: string;
  product_name: string;
  product_type: 'cylinder' | 'equipment';
  serial_number?: string;
  status: string;
  customer_name?: string;
  rental_date?: string;
  return_date?: string;
  deposit_name?: string;
}

export const RentalItemsReportView = () => {
  const { companyId } = useMultiTenant();
  const [items, setItems] = useState<RentalItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  useEffect(() => {
    if (companyId) {
      loadItems();
    }
  }, [companyId]);

  const loadItems = async () => {
    try {
      // Carregar equipamentos
      const { data: equipmentData, error: eqError } = await supabase
        .from('equipment')
        .select(`
          id,
          serial_number,
          status,
          name,
          current_deposit_id,
          deposits:current_deposit_id(name),
          product_id,
          products:product_id(name)
        `)
        .eq('company_id', companyId)
        .not('product_id', 'is', null);

      if (eqError) throw eqError;

      // Carregar cilindros
      const { data: cylinderData, error: cylError } = await supabase
        .from('cylinders')
        .select(`
          id,
          serial_number,
          status,
          current_deposit_id,
          deposits:current_deposit_id(name),
          product_id,
          products:product_id(name)
        `)
        .eq('company_id', companyId)
        .not('product_id', 'is', null);

      if (cylError) throw cylError;

      const combinedItems: RentalItem[] = [
        ...(equipmentData || []).map((e: any) => ({
          id: e.id,
          product_name: e.products?.name || e.name || 'Sem nome',
          product_type: 'equipment' as const,
          serial_number: e.serial_number,
          status: e.status,
          deposit_name: e.deposits?.name
        })),
        ...(cylinderData || []).map((c: any) => ({
          id: c.id,
          product_name: c.products?.name || 'Sem nome',
          product_type: 'cylinder' as const,
          serial_number: c.serial_number,
          status: c.status,
          deposit_name: c.deposits?.name
        }))
      ];

      setItems(combinedItems);
    } catch (error) {
      console.error('Erro ao carregar itens de locação:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const statusMap: Record<string, { label: string; variant: any }> = {
      available: { label: '🟢 Disponível', variant: 'default' },
      rented: { label: '🔴 Locado', variant: 'destructive' },
      loaned: { label: '🟡 Emprestado', variant: 'secondary' },
      maintenance: { label: '🔧 Manutenção', variant: 'outline' },
      sold: { label: '💰 Vendido', variant: 'secondary' },
      inactive: { label: '⚫ Inativo', variant: 'secondary' }
    };

    const config = statusMap[status] || { label: status, variant: 'outline' };
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  const getTypeBadge = (type: string) => {
    return type === 'cylinder' ? (
      <Badge variant="secondary">Cilindro</Badge>
    ) : (
      <Badge variant="outline">Equipamento</Badge>
    );
  };

  const filteredItems = items.filter(item => {
    const matchesSearch = 
      item.product_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.serial_number?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesType = typeFilter === 'all' || item.product_type === typeFilter;
    const matchesStatus = statusFilter === 'all' || item.status === statusFilter;

    return matchesSearch && matchesType && matchesStatus;
  });

  const stats = {
    total: items.length,
    available: items.filter(i => i.status === 'available').length,
    rented: items.filter(i => i.status === 'rented').length,
    loaned: items.filter(i => i.status === 'loaned').length
  };

  if (loading) {
    return <div className="p-8 text-center">Carregando itens de locação...</div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold mb-2">📦 Relatório de Itens de Locação</h1>
        <p className="text-muted-foreground">
          Visualize todos os equipamentos e cilindros de locação com seus status
        </p>
      </div>

      <div className="grid grid-cols-4 gap-4">
        <Card className="p-4">
          <p className="text-sm text-muted-foreground">Total de Itens</p>
          <p className="text-2xl font-bold">{stats.total}</p>
        </Card>
        <Card className="p-4">
          <p className="text-sm text-muted-foreground">Disponíveis</p>
          <p className="text-2xl font-bold text-green-600">{stats.available}</p>
        </Card>
        <Card className="p-4">
          <p className="text-sm text-muted-foreground">Locados</p>
          <p className="text-2xl font-bold text-red-600">{stats.rented}</p>
        </Card>
        <Card className="p-4">
          <p className="text-sm text-muted-foreground">Emprestados</p>
          <p className="text-2xl font-bold text-orange-600">{stats.loaned}</p>
        </Card>
      </div>

      <div className="flex gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar por produto ou série..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>

        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Tipo" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos os Tipos</SelectItem>
            <SelectItem value="cylinder">Cilindros</SelectItem>
            <SelectItem value="equipment">Equipamentos</SelectItem>
          </SelectContent>
        </Select>

        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos os Status</SelectItem>
            <SelectItem value="available">Disponível</SelectItem>
            <SelectItem value="rented">Locado</SelectItem>
            <SelectItem value="loaned">Emprestado</SelectItem>
            <SelectItem value="maintenance">Manutenção</SelectItem>
            <SelectItem value="sold">Vendido</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Produto</TableHead>
              <TableHead>Tipo</TableHead>
              <TableHead>N° de Série</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Depósito</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredItems.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                  <Package className="h-12 w-12 mx-auto mb-2 opacity-20" />
                  Nenhum item encontrado
                </TableCell>
              </TableRow>
            ) : (
              filteredItems.map((item) => (
                <TableRow key={item.id}>
                  <TableCell className="font-medium">{item.product_name}</TableCell>
                  <TableCell>{getTypeBadge(item.product_type)}</TableCell>
                  <TableCell>
                    {item.serial_number || (
                      <span className="text-muted-foreground text-sm">Não rastreado</span>
                    )}
                  </TableCell>
                  <TableCell>{getStatusBadge(item.status)}</TableCell>
                  <TableCell>
                    {item.deposit_name || (
                      <span className="text-muted-foreground text-sm">-</span>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
};